import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ChatService } from './chat.service';
import { environment } from '../../environments/environment'
import { GroupedSessions, SessionResponse, SessionResponseShort } from '../interfaces/sessions'
import { catchError, finalize, map, Observable, of } from 'rxjs'
import { ApiResponse } from '../interfaces/api'
import { groupSessionsByDate } from '../utils/sessions.helper';
import { StateService } from './state.service';
import { AuthTokenService } from './auth-token.service';
import { from, switchMap } from 'rxjs';
import { throwError } from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class SideBarService {
  public sessionList: any[] = [];
  public chatList: SessionResponse[] = [];
  public currentChat: any;
  public groupedChat: any[] = [];
  public storageSession!: string;
  public parentUrl!: string;
  public isLoading: boolean = false;

  private readonly _shortGroupedSessions = signal<GroupedSessions[]>([]);
  public readonly shortGroupedSessions = this._shortGroupedSessions.asReadonly();
  private readonly _isShortGroupedChatsLoading = signal<boolean>(false);
  public readonly isShortGroupedChatsLoading = this._isShortGroupedChatsLoading.asReadonly();
  private readonly stateService = inject(StateService);
  private readonly authTokenService = inject(AuthTokenService);
  private shortSessionsRetryCount = 0;

  constructor(
    private http: HttpClient,
    private chatService: ChatService,
  ) {
  }

  loadShortSessionsInfo(): void {
    // Validate schema is available before making the request
    const schema = this.stateService.schema || this.chatService.schema;
    if (!schema) {
      console.warn('Cannot load sessions: x-schema is not set. Waiting for schema to be initialized...');
      this._isShortGroupedChatsLoading.set(false);
      // In some boot flows, sidebar loads before query params are applied.
      // Retry briefly to avoid "no network requests" confusion.
      if (this.shortSessionsRetryCount < 20) {
        this.shortSessionsRetryCount += 1;
        setTimeout(() => this.loadShortSessionsInfo(), 250);
      } else {
        this.shortSessionsRetryCount = 0;
      }
      return;
    }

    this.shortSessionsRetryCount = 0;
    this._isShortGroupedChatsLoading.set(true);
    this.getShortSessionsInfoApi()
      .pipe(
        map(res => groupSessionsByDate(res.data || [])),
        finalize(() => {
          this._isShortGroupedChatsLoading.set(false);
        }),
        catchError((error) => {
          console.error(error);
          return of([] as any);
        }),
      )
      .subscribe({
        next: (groupedSessions) => {
          this._shortGroupedSessions.set(groupedSessions);
          if (this.storageSession) {
            this.setCurrentSession(this.storageSession)
          }
        },
        error: (error) => {
          console.error('Failed to load sessions:', error);
          this._shortGroupedSessions.set([]);
        }
      });
  }

  private getShortSessionsInfoApi(): Observable<ApiResponse<SessionResponseShort[]>> {
    const schema = this.chatService.schema || '';

    const userId = this.chatService.user_id;

    const params: Record<string, any> = {
      flowId: this.chatService.flowId || '',
      includeMessages: false,
    }

    // Add userId to params if available
    if (userId) {
      params['userId'] = userId;
      console.log('Adding userId to session query:', userId);
    } else {
      console.warn('No userId found - sessions will not be filtered by user');
    }

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        if (typeof token !== 'string' || token.trim().length === 0) {
          return throwError(() => new Error('Missing access token for sessions request'));
        }
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-schema': schema,
        });
        return this.http.get<ApiResponse<SessionResponseShort[]>>(
          `${environment.baseURL}/v1/session/all`,
          { params, headers },
        );
      }),
    );
  }

  setCurrentSession(session_id: string) {
    this.storageSession = '';
    this.chatService.messagesSubject.next([]);
    if (this._shortGroupedSessions().length) {
      this.chatService.loadChatMessages(session_id);
      this.currentChat = session_id;
    }
  }

}
